<template>
  <div class="alipay-container">
    <!-- 面包屑导航 -->
    <div class="breadcrumb-wrapper">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/Welcome' }">
          <i class="el-icon-s-home"></i> 首页
        </el-breadcrumb-item>
        <el-breadcrumb-item>
          <i class="el-icon-goods"></i> 产品销售统计
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- 卡片内容 -->
    <el-card class="alipay-card" shadow="never">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <div class="search-row">
          <div class="search-item">
            <span class="search-label">门店名称</span>
            <el-input 
              v-model="searchForm.shop_name" 
              placeholder="请输入门店名称"
              prefix-icon="el-icon-search"
              clearable
              size="small"
              class="search-input"
            ></el-input>
          </div>
          <div class="search-item">
            <span class="search-label">产品名称</span>
            <el-input 
              v-model="searchForm.product_name" 
              placeholder="请输入产品名称"
              prefix-icon="el-icon-goods"
              clearable
              size="small"
              class="search-input"
            ></el-input>
          </div>
          <div class="search-actions">
            <el-button type="primary" @click="searchSubmit" icon="el-icon-search" size="small">搜索</el-button>
            <el-button @click="resetSearch" icon="el-icon-refresh-right" size="small">重置</el-button>
            <el-button type="success" @click="exportExcel" icon="el-icon-download" size="small">导出</el-button>
          </div>
        </div>
      </div>

      <!-- 统计卡片 -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-icon total"><i class="el-icon-shop"></i></div>
          <div class="stat-info">
            <div class="stat-value">{{ shopCount }}</div>
            <div class="stat-label">销售门店数</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon product"><i class="el-icon-goods"></i></div>
          <div class="stat-info">
            <div class="stat-value">{{ productCount }}</div>
            <div class="stat-label">销售产品数</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon total-sales"><i class="el-icon-sold-out"></i></div>
          <div class="stat-info">
            <div class="stat-value">{{ totalQuantity }}</div>
            <div class="stat-label">总销售数量</div>
          </div>
        </div>
      </div>

      <!-- 表格 -->
      <div class="table-wrapper">
        <el-table
          ref="multipleTable"
          :data="tableLoadData"
          stripe
          border
          size="medium"
          :header-cell-style="headerCellStyle"
          :row-style="rowStyle"
          v-loading="loading"
          element-loading-text="加载中..."
          @selection-change="handleSelectionChange"
          show-summary
          :summary-method="getSummaries"
        >
          <el-table-column type="selection" width="50" align="center"></el-table-column>
          <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod"></el-table-column>
          
          <el-table-column label="门店信息" min-width="200">
            <template slot-scope="scope">
              <div class="shop-info">
                <div class="shop-name">
                  <i class="el-icon-shop"></i>
                  <span>{{ scope.row.shop_name || '-' }}</span>
                </div>
                <div class="shop-contact" v-if="scope.row.contact_name || scope.row.mobile">
                  <i class="el-icon-phone"></i>
                  <span>{{ scope.row.contact_name || '' }} {{ scope.row.mobile || '' }}</span>
                </div>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column label="产品信息" min-width="180">
            <template slot-scope="scope">
              <div class="product-info">
                <div class="product-name">
                  <i class="el-icon-goods"></i>
                  <span>{{ scope.row.product_name || '-' }}</span>
                </div>
                <div class="product-unit" v-if="scope.row.unit">
                  <el-tag size="small" type="info" effect="plain">
                    单位：{{ scope.row.unit }}
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column label="销售数量" width="140" align="center" prop="sell_total_count">
            <template slot-scope="scope">
              <span class="quantity">{{ formatNumber(scope.row.sell_total_count) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="销售金额" width="140" align="center">
            <template slot-scope="scope">
              <span class="amount">¥{{ formatNumber(scope.row.total_amount) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="占⽐" width="100" align="center">
            <template slot-scope="scope">
              <el-progress 
                :percentage="getPercentage(scope.row.sell_total_count)" 
                :stroke-width="8"
                :show-text="false"
              ></el-progress>
              <span class="percentage">{{ getPercentage(scope.row.sell_total_count) }}%</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 空状态 -->
      <div v-if="tableLoadData.length === 0 && !loading" class="empty-state">
        <i class="el-icon-goods"></i>
        <p>暂无销售数据</p>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="total > 0">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 30, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :pager-count="5"
        ></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import moment from 'moment'
import * as XLSX from 'xlsx'

export default {
  name: "productSalesStat",
  data() {
    return {
      loading: false,
      
      // 搜索表单
      searchForm: {
        shop_name: "",
        product_name: ""
      },
      
      // 统计数据
      shopCount: 0,
      productCount: 0,
      totalQuantity: 0,
      
      // 表格数据
      tableLoadData: [],
      selectedRows: [],
      pageNum: 1,
      pageSize: 10,
      total: 0,
      
      // 总计数量
      totalAllQuantity: 0
    }
  },
  created() {
    this.DataList()
  },
  filters: {
    dateFormat(time) {
      if (!time) return ""
      return moment(time * 1000).format("YYYY-MM-DD HH:mm:ss")
    }
  },
  methods: {
    headerCellStyle() {
      return {
        background: '#f5f7fa',
        color: '#1f2d3d',
        fontWeight: '500',
        fontSize: '14px',
        borderBottom: '1px solid #e4e7ed'
      }
    },
    rowStyle() { return { cursor: 'pointer' } },
    
    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      if (num >= 10000) {
        return (num / 10000).toFixed(1) + 'w'
      }
      return num.toLocaleString()
    },
    
    getPercentage(value) {
      if (!this.totalAllQuantity || this.totalAllQuantity === 0) return 0
      return ((value / this.totalAllQuantity) * 100).toFixed(1)
    },
    
    handleSelectionChange(val) {
      this.selectedRows = val
    },
    
    calculateStats() {
      if (!this.tableLoadData.length) {
        this.shopCount = 0
        this.productCount = 0
        this.totalQuantity = 0
        this.totalAllQuantity = 0
        return
      }
      
      const uniqueShops = new Set()
      const uniqueProducts = new Set()
      let totalQty = 0
      
      this.tableLoadData.forEach(item => {
        if (item.shop_name) uniqueShops.add(item.shop_name)
        if (item.product_name) uniqueProducts.add(item.product_name)
        totalQty += parseFloat(item.sell_total_count) || 0
      })
      
      this.shopCount = uniqueShops.size
      this.productCount = uniqueProducts.size
      this.totalQuantity = totalQty
      this.totalAllQuantity = totalQty
    },
    
    resetSearch() {
      this.searchForm = { shop_name: "", product_name: "" }
      this.pageNum = 1
      this.DataList()
      this.$message.success('已重置搜索条件')
    },
    
    indexMethod(index) { return (this.pageNum - 1) * this.pageSize + index + 1 },
    
    DataList() {
      this.loading = true
      const param = {
        company_id: sessionStorage.getItem('company_id'),
        access_token: sessionStorage.getItem('token')
      }
      if (this.searchForm.shop_name) param.shop_name = this.searchForm.shop_name
      if (this.searchForm.product_name) param.product_name = this.searchForm.product_name
      
      this.$axios.findWarehouseProductSellList(param).then(res => {
        this.loading = false
        if (res.code == 200) {
          this.tableLoadData = res.data.list || []
          this.total = this.res.data.total
          this.calculateStats()
        }
      }).catch(error => {
        this.loading = false
        console.log(error)
      })
    },
    
    handleSizeChange(newSize) {
      this.pageSize = newSize
      this.pageNum = 1
      this.DataList()
    },
    
    handleCurrentChange(newPage) {
      this.pageNum = newPage
      this.DataList()
    },
    
    searchSubmit() {
      this.pageNum = 1
      this.DataList()
    },
    
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        if (column.property === 'sell_total_count') {
          const total = data.reduce((prev, curr) => prev + (parseFloat(curr.sell_total_count) || 0), 0)
          sums[index] = this.formatNumber(total)
        } else if (column.property === 'total_amount') {
          const total = data.reduce((prev, curr) => prev + (parseFloat(curr.total_amount) || 0), 0)
          sums[index] = `¥${this.formatNumber(total)}`
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
    
    exportExcel() {
      if (!this.tableLoadData.length) {
        this.$message.warning('暂无数据可导出')
        return
      }
      
      const exportData = this.tableLoadData.map((row, index) => ({
        '序号': index + 1,
        '门店名称': row.shop_name || '--',
        '联系电话': row.mobile || '--',
        '产品名称': row.product_name || '--',
        '单位': row.unit || '--',
        '销售数量': row.sell_total_count || 0,
        '销售金额': row.total_amount || 0
      }))
      
      const worksheet = XLSX.utils.json_to_sheet(exportData)
      worksheet['!cols'] = [
        { wch: 8 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 15 }
      ]
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, '产品销售统计')
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `产品销售统计_${moment().format('YYYYMMDDHHmmss')}.xlsx`
      link.click()
      URL.revokeObjectURL(link.href)
      this.$message.success(`成功导出 ${this.tableLoadData.length} 条数据`)
    }
  }
}
</script>

<style lang="scss" scoped>
.alipay-container {
  padding: 16px 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.breadcrumb-wrapper {
  margin-bottom: 16px;
  .el-breadcrumb { font-size: 14px; i { margin-right: 4px; } }
}

.alipay-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  &:hover { box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.08); }
  .el-card__body { padding: 20px 24px; }
}

// 搜索栏
.search-bar {
  background: #f8f9fc;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  
  .search-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
  }
  
  .search-item {
    display: flex;
    align-items: center;
    gap: 8px;
    .search-label { font-size: 13px; color: #606266; white-space: nowrap; min-width: 80px; text-align: right; }
    .search-input { width: 200px; }
  }
  
  .search-actions {
    display: flex;
    gap: 10px;
    margin-left: auto;
    .el-button { border-radius: 20px; padding: 8px 18px; }
    .el-button--primary { background: #1677ff; border-color: #1677ff; &:hover { background: #4096ff; } }
    .el-button--success { background: #00b578; border-color: #00b578; &:hover { background: #1acd8c; } }
  }
}

// 统计卡片
.stat-cards {
  display: flex; gap: 20px; margin-bottom: 24px; flex-wrap: wrap;
  .stat-card {
    flex: 1; min-width: 150px; background: linear-gradient(135deg, #fff 0%, #f8f9fc 100%);
    border-radius: 16px; padding: 16px 20px; display: flex; align-items: center; gap: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); transition: all 0.3s ease;
    &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08); }
    .stat-icon {
      width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; color: #fff;
      &.total { background: linear-gradient(135deg, #1677ff, #4096ff); }
      &.product { background: linear-gradient(135deg, #00b578, #1acd8c); }
      &.total-sales { background: linear-gradient(135deg, #e6a23c, #ebb563); }
    }
    .stat-info {
      .stat-value { font-size: 28px; font-weight: 700; color: #1f2d3d; }
      .stat-label { font-size: 13px; color: #8c8f98; margin-top: 4px; }
    }
  }
}

// 表格样式
.table-wrapper { margin-bottom: 20px; min-height: 350px; }
.el-table { border-radius: 12px; overflow: hidden; &::before { display: none; } th { background: #fafbfc !important; font-weight: 500; } td { border-bottom: 1px solid #f0f0f0; } .el-table__row { transition: background 0.2s; &:hover { background: #f8f9fc; } } }

.shop-info {
  .shop-name { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; i { font-size: 16px; color: #1677ff; } span { font-weight: 500; } }
  .shop-contact { display: flex; align-items: center; gap: 6px; i { font-size: 12px; color: #00b578; } span { font-size: 12px; color: #8c8f98; } }
}

.product-info {
  .product-name { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; i { font-size: 16px; color: #e6a23c; } span { font-weight: 500; } }
  .product-unit { margin-top: 4px; }
}

.quantity {
  font-weight: 600;
  color: #e6a23c;
  font-size: 16px;
}

.amount {
  color: #ff4d4f;
  font-weight: 600;
}

.percentage {
  margin-left: 8px;
  font-size: 12px;
  color: #8c8f98;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #c0c4cc;
  i { font-size: 64px; }
  p { margin-top: 16px; font-size: 14px; }
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  .el-pagination .el-pager li.active { background: #1677ff; color: #fff; }
}
</style>